import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import MobileNav from '@/components/MobileNav';
import { BookOpen, Trophy, Zap, Star, Lock, ChevronRight, TrendingUp, Target, Flame, Medal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useTier } from '@/context/TierContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVEL_COLORS = {
  beginner: 'text-green-400 border-green-400/30 bg-green-400/10',
  intermediate: 'text-yellow-400 border-yellow-400/30 bg-yellow-400/10',
  advanced: 'text-red-400 border-red-400/30 bg-red-400/10',
};

const LEVEL_LABELS = {
  beginner: 'Débutant',
  intermediate: 'Intermédiaire',
  advanced: 'Avancé',
};

export default function Academie() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { hasAccess } = useTier();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [dailyLesson, setDailyLesson] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [modulesRes, progressRes, dailyRes, lbRes] = await Promise.all([
        axios.get(`${API}/education/modules`, { headers }),
        axios.get(`${API}/education/progress`, { headers }),
        axios.get(`${API}/education/daily-lesson`, { headers }),
        axios.get(`${API}/education/leaderboard`, { headers }),
      ]);
      setModules(Object.values(modulesRes.data));
      setProgress(progressRes.data);
      setDailyLesson(dailyRes.data);
      setLeaderboard(lbRes.data?.leaderboard || []);
    } catch (err) {
      console.error('Failed to fetch academy data:', err);
    } finally {
      setLoading(false);
    }
  };

  const isLessonCompleted = (lessonId) => {
    return progress?.completed_lessons?.includes(lessonId) || false;
  };

  const getModuleProgress = (module) => {
    if (!module.lessons || module.lessons.length === 0) return 0;
    const completed = module.lessons.filter(l => isLessonCompleted(l.id)).length;
    return Math.round((completed / module.lessons.length) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#ff6b35] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Chargement de l'Académie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="academie" />

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-10 animate-slide-up">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#ff6b35] to-[#ff8555] flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-[#0a0a0a]" />
            </div>
            <span className="text-[#ff6b35] font-semibold text-sm uppercase tracking-widest">Académie Do Not Bull</span>
          </div>
          <h1 className="text-4xl font-bold mb-2">Apprends à investir <span className="gradient-text">intelligemment</span></h1>
          <p className="text-gray-400 text-lg">Des cours structurés pour passer de débutant à investisseur confiant</p>
        </div>

        {/* Stats Row */}
        {progress && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <div data-testid="stat-xp" className="glass rounded-2xl p-5 text-center">
              <Zap className="w-6 h-6 text-[#ff6b35] mx-auto mb-2" />
              <div className="text-2xl font-bold text-[#ff6b35]">{progress.total_xp}</div>
              <div className="text-xs text-gray-400 mt-1">XP Total</div>
            </div>
            <div data-testid="stat-level" className="glass rounded-2xl p-5 text-center">
              <Star className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-yellow-400">{progress.level_info?.current?.icon} {progress.level_info?.current?.name}</div>
              <div className="text-xs text-gray-400 mt-1">Niveau {progress.level_info?.current?.level}</div>
            </div>
            <div data-testid="stat-streak" className="glass rounded-2xl p-5 text-center">
              <Flame className="w-6 h-6 text-orange-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-orange-400">{progress.streak || 0}</div>
              <div className="text-xs text-gray-400 mt-1">Jours consécutifs</div>
            </div>
            <div data-testid="stat-lessons" className="glass rounded-2xl p-5 text-center">
              <Trophy className="w-6 h-6 text-purple-400 mx-auto mb-2" />
              <div className="text-2xl font-bold text-purple-400">{progress.completed_lessons?.length || 0}</div>
              <div className="text-xs text-gray-400 mt-1">Leçons complétées</div>
            </div>
          </div>
        )}

        {/* Level Progress Bar */}
        {progress?.level_info?.next && (
          <div className="glass rounded-2xl p-6 mb-10">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <span className="text-white font-semibold">{progress.level_info.current?.icon} {progress.level_info.current?.name}</span>
                <ChevronRight className="w-4 h-4 text-gray-500" />
                <span className="text-gray-400">{progress.level_info.next?.icon} {progress.level_info.next?.name}</span>
              </div>
              <span className="text-[#ff6b35] font-semibold">{progress.level_info.progress}%</span>
            </div>
            <Progress value={progress.level_info.progress} className="h-3" />
            <p className="text-xs text-gray-500 mt-2">
              {progress.total_xp} / {progress.level_info.next?.min_xp} XP pour atteindre {progress.level_info.next?.name}
            </p>
          </div>
        )}

        {/* Daily Lesson */}
        {dailyLesson && (
          <div className="mb-10">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" />
              Leçon du Jour
            </h2>
            <div
              data-testid="daily-lesson-card"
              onClick={() => navigate(`/academie/daily`)}
              className="glass rounded-2xl p-6 cursor-pointer hover:border-[#ff6b35]/40 border border-transparent transition-all group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-[#ff6b35] font-semibold uppercase tracking-widest mb-1">{dailyLesson.category}</div>
                  <h3 className="text-xl font-bold text-white group-hover:text-[#ff6b35] transition-colors">{dailyLesson.title}</h3>
                  <p className="text-gray-400 text-sm mt-1">+{dailyLesson.xp_reward} XP • Quiz inclus</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#ff6b35]/10 flex items-center justify-center group-hover:bg-[#ff6b35]/20 transition-colors">
                  <ChevronRight className="w-6 h-6 text-[#ff6b35]" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modules Grid */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#ff6b35]" />
              Modules de Formation
            </h2>
            <Button
              data-testid="go-to-simulator"
              onClick={() => navigate('/simulator')}
              variant="outline"
              className="border-[#ff6b35]/30 text-[#ff6b35] hover:bg-[#ff6b35]/10"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Simulateur de Trading
            </Button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {modules.map((mod, idx) => {
              const modProgress = getModuleProgress(mod);
              const isLocked = mod.tier_required !== 'free' && !hasAccess(mod.tier_required);
              const isStarted = modProgress > 0;
              const isCompleted = modProgress === 100;

              return (
                <div
                  key={mod.id}
                  data-testid={`module-card-${mod.id}`}
                  onClick={() => !isLocked && navigate(`/academie/module/${mod.id}`)}
                  className={`glass rounded-2xl p-6 border transition-all ${
                    isLocked
                      ? 'border-gray-700/30 opacity-60 cursor-not-allowed'
                      : isCompleted
                      ? 'border-green-400/30 cursor-pointer hover:border-green-400/50'
                      : 'border-transparent cursor-pointer hover:border-[#ff6b35]/40'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{mod.icon.replace(/[🟢🟡🟠🔴]/g, '') || '📚'}</span>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${LEVEL_COLORS[mod.level] || LEVEL_COLORS.beginner}`}>
                            {LEVEL_LABELS[mod.level] || mod.level}
                          </span>
                          {isCompleted && <span className="text-xs font-semibold text-green-400">Complété ✓</span>}
                        </div>
                        <h3 className="font-bold text-white">{mod.title}</h3>
                      </div>
                    </div>
                    {isLocked ? (
                      <Lock className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </div>

                  <p className="text-gray-400 text-sm mb-4">{mod.description}</p>

                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <span>{mod.lessons_count} leçons • {mod.duration}</span>
                    {isStarted && <span className="text-[#ff6b35]">{modProgress}%</span>}
                  </div>

                  {isStarted && (
                    <Progress value={modProgress} className="h-2" />
                  )}

                  {!isStarted && !isLocked && (
                    <div className="mt-2">
                      <span className="text-xs text-[#ff6b35] font-semibold">
                        {idx === 0 ? 'Commencer' : 'Débloquer'} →
                      </span>
                    </div>
                  )}

                  {isLocked && (
                    <div className="mt-2">
                      <span className="text-xs text-gray-500">Abonnement requis</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Badges */}
        {progress?.badges?.length > 0 && (
          <div className="glass rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              Mes Badges
            </h2>
            <div className="flex flex-wrap gap-3">
              {progress.badges.map((badge, i) => (
                <div key={i} data-testid={`badge-${i}`} className="flex items-center gap-2 px-4 py-2 bg-yellow-400/10 border border-yellow-400/30 rounded-full">
                  <span className="text-yellow-400 font-semibold text-sm">{badge}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="glass rounded-2xl p-6">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
              <Medal className="w-5 h-5 text-orange-400" />
              Classement des Apprenants
            </h2>
            <div className="space-y-2">
              {leaderboard.map((learner, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-4 p-3 rounded-xl transition-all ${
                    learner.is_current_user
                      ? 'bg-[#ff6b35]/10 border border-[#ff6b35]/30'
                      : 'bg-[#0a0a0a]/40 hover:bg-[#0a0a0a]/60'
                  }`}
                >
                  {/* Rank */}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm flex-shrink-0 ${
                    i === 0 ? 'bg-yellow-400/20 text-yellow-400' :
                    i === 1 ? 'bg-gray-400/20 text-gray-300' :
                    i === 2 ? 'bg-orange-700/20 text-orange-600' :
                    'bg-gray-800 text-gray-500'
                  }`}>
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${learner.rank}`}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-bold text-sm truncate ${learner.is_current_user ? 'text-[#ff6b35]' : 'text-white'}`}>
                        {learner.name} {learner.is_current_user && '(vous)'}
                      </span>
                      <span className="text-xs">{learner.level_icon}</span>
                      <span className="text-xs text-gray-500">{learner.level}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      <span>{learner.lessons_count} leçons</span>
                      <span>🔥 {learner.streak} jours</span>
                      <span>{learner.badges_count} badges</span>
                    </div>
                  </div>

                  {/* XP */}
                  <div className="text-right flex-shrink-0">
                    <div className="flex items-center gap-1 text-[#ff6b35] font-bold text-sm">
                      <Zap className="w-3 h-3" />
                      {learner.xp.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">XP</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
